import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  SimpleGrid,
  AspectRatio,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  useColorModeValue as mode,
} from "@chakra-ui/react";

import { FaPlay } from "react-icons/fa";

// Explainer reel, hosted on the CDN (origin: schnerp-cdn/public/videos).
// Transcoded from the client's 167 MB .mov to a 1080p H.264 MP4 (~34 MB,
// +faststart). Bump the filename version if the reel is ever re-cut.
const VIDEO_URL = "https://cdn.schnerp.com/public/videos/infomercial-v1.mp4";
// Hand-picked poster: a still from the reel. Single canonical file, updated in
// place (cached 1 day, not immutable) — re-extract and overwrite it to swap
// frames.
const POSTER_URL =
  "https://cdn.schnerp.com/public/videos/infomercial-poster.webp";

const HEADING = "How it works";
const SUBTITLE =
  "You tell us where you want to camp, we do the watching, and you book the spot.";

// Three-step explainer. Deliberately generic and free of scan-cadence claims —
// one definition shared by every landing page.
const STEPS = [
  {
    title: "Create a request",
    body: "Pick your spot, your dates, and preferred sites.",
  },
  {
    title: "Wait for a notification",
    body: "We watch the reservation site around the clock.",
  },
  {
    title: "Book that spot",
    body: "We send you a direct link to book on the reservation website.",
  },
];

// One step in the timeline. The numbered token and a connector line live in a
// stretched left rail, so the line runs circle-to-circle and the steps read as
// a single connected sequence.
function Step({ n, title, body, isLast }) {
  return (
    <HStack align="stretch" spacing={4}>
      <VStack flex="none" spacing={1.5} align="center">
        <Flex
          flex="none"
          boxSize="32px"
          align="center"
          justify="center"
          borderRadius="full"
          bg="accent"
          color="inverted"
          fontWeight="bold"
          fontSize="sm"
          aria-hidden="true"
        >
          {n}
        </Flex>
        {!isLast && (
          <Box w="2px" flex="1" minH="12px" bg="border" borderRadius="full" />
        )}
      </VStack>
      <Box pb={isLast ? 0 : 6}>
        <Text fontWeight="bold" fontSize="md">
          {title}
        </Text>
        <Text color="muted" fontSize="sm" lineHeight="1.5">
          {body}
        </Text>
      </Box>
    </HStack>
  );
}

// The click-to-play video frame: poster thumbnail, a play affordance that
// scales on hover (the whole stage is the hover group), and a duration badge.
function VideoStage({ onOpen }) {
  return (
    <Box position="relative" w="100%" role="group">
      <Box
        as="button"
        type="button"
        onClick={onOpen}
        aria-label="Play the Schnerp infomercial"
        display="block"
        w="100%"
        borderRadius={{ base: "xl", md: "2xl" }}
        overflow="hidden"
        borderWidth="1px"
        borderColor="border"
        boxShadow={mode("lg", "dark-lg")}
        cursor="pointer"
        transition="box-shadow 0.2s ease"
        _groupHover={{ boxShadow: mode("xl", "dark-lg") }}
      >
        <AspectRatio ratio={16 / 9}>
          <Box
            as="img"
            src={POSTER_URL}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </AspectRatio>
      </Box>

      {/* Center scrim + play affordance — visual only; clicks pass through */}
      <Flex
        position="absolute"
        inset="0"
        align="center"
        justify="center"
        pointerEvents="none"
        sx={{
          background:
            "radial-gradient(40% 55% at 50% 50%, rgba(0,0,0,0.35), transparent 70%)",
        }}
      >
        <Flex
          boxSize={{ base: "48px", md: "64px" }}
          borderRadius="full"
          bg="whiteAlpha.900"
          align="center"
          justify="center"
          boxShadow="0 12px 36px -10px rgba(0,0,0,0.5)"
          transition="transform 0.2s ease"
          _groupHover={{ transform: "scale(1.06)" }}
        >
          <Box
            as={FaPlay}
            boxSize={{ base: "22px", md: "24px" }}
            color="gray.900"
            ml="4px"
          />
        </Flex>
      </Flex>

      {/* Duration + label badge */}
      <HStack
        position="absolute"
        left="8px"
        bottom="8px"
        spacing={2}
        px={3}
        py={1.5}
        borderRadius="full"
        bg="blackAlpha.700"
        backdropFilter="blur(6px)"
        color="white"
        fontSize="xs"
        fontWeight="bold"
        pointerEvents="none"
      >
        <Box bg="warning" color="gray.900" borderRadius="md" px={1.5}>
          1:15
        </Box>
        <Text>Watch the infomercial</Text>
      </HStack>
    </Box>
  );
}

/**
 * "How it works" — the infomercial does the talking. A big, full-width video is
 * the centerpiece (show, don't tell), with the three steps as a connected
 * timeline beside it. Zero-config: the reel, poster, copy, and steps are fixed,
 * shared by every landing page (home, country, featured, partner).
 */
export function HowItWorksVideo() {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box w="100%" maxW="container.lg" mx="auto" px={{ base: 4, md: 6 }}>
      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={{ base: 8, md: 12 }}
        alignItems="center"
      >
        {/* Heading + step timeline */}
        <VStack align="flex-start" spacing={6}>
          <VStack align="flex-start" spacing={2}>
            <Heading
              as="h2"
              size={{ base: "sm", md: "md" }}
              fontWeight="extrabold"
            >
              {HEADING}
            </Heading>
            <Text color="muted" fontSize={{ base: "md", md: "lg" }}>
              {SUBTITLE}
            </Text>
          </VStack>

          <VStack align="stretch" spacing={0} w="100%">
            {STEPS.map((step, i) => (
              <Step
                key={step.title}
                n={i + 1}
                isLast={i === STEPS.length - 1}
                {...step}
              />
            ))}
          </VStack>
        </VStack>

        {/* Video — right on desktop, but moves to the TOP when stacked on
            mobile (order -1) so the hook leads on small screens. */}
        <Box order={{ base: -1, md: 0 }} w="100%">
          <VideoStage onOpen={onOpen} />
        </Box>
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(6px)" />
        {/* overflow="visible" so the close button can float above the frame.
            The native player's mute/PiP/fullscreen controls hug every edge of
            the video (top-right is the mute button on mobile Chrome), so an
            in-frame "X" collides with them — a tap meant to unmute closes the
            modal. Lift it into the overlay above the top-right corner instead;
            backdrop tap and ESC still close as fallbacks.

            maxW caps the 16:9 frame by viewport HEIGHT as well as width (56rem
            = old size="4xl"). Without the height term, a landscape phone sizes
            the video to full width, making it taller than the screen — it then
            overflows top and bottom, pushing the close button off-screen and
            leaving no backdrop to tap (you get stuck). Reserving 7rem keeps a
            band of tappable overlay above and below in every orientation. */}
        <ModalContent
          bg="black"
          overflow="visible"
          mx={4}
          maxW="min(56rem, calc((100dvh - 7rem) * 16 / 9))"
        >
          <ModalCloseButton
            color="whiteAlpha.900"
            bg="blackAlpha.700"
            _hover={{ bg: "blackAlpha.800" }}
            borderRadius="full"
            top={-12}
            right={0}
            zIndex={2}
          />
          <ModalBody p={0} borderRadius="md" overflow="hidden">
            <AspectRatio ratio={16 / 9}>
              <Box
                as="video"
                src={VIDEO_URL}
                poster={POSTER_URL}
                controls
                autoPlay
                playsInline
                sx={{ width: "100%", height: "100%" }}
              />
            </AspectRatio>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
